/** Shared layout helpers for the marketing landing (PhishFence light style). */
import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { clsx } from 'clsx';

/** A full-width section with a centered, max-w-6xl inner container. */
export function Section({
  id,
  children,
  className,
}: {
  id?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section id={id} className={clsx('px-4 py-16 sm:px-6 sm:py-20', className)}>
      <div className="mx-auto max-w-6xl">{children}</div>
    </section>
  );
}

/** Small brand-tinted section label (PhishFence eyebrow). */
export function Eyebrow({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={clsx(
        'text-xs font-semibold uppercase tracking-widest text-brand-700',
        className,
      )}
    >
      {children}
    </span>
  );
}

/** A thin hairline rule for separating sections. */
export function Rule({ className }: { className?: string }) {
  return <hr className={clsx('border-0 border-t border-slate-200', className)} />;
}

/** The product wordmark: brand-700 R monogram + Rev/Base split, used in the
 * nav, login, and footer so the brand reads identically everywhere. Pass
 * `tone="dark"` on the slate-900 footer band so the slate part flips to white. */
export function Wordmark({
  tone = 'light',
  className,
}: {
  tone?: 'light' | 'dark';
  className?: string;
}) {
  return (
    <span
      className={clsx(
        'flex items-center gap-2 font-display text-lg font-bold tracking-tight',
        tone === 'dark' ? 'text-white' : 'text-slate-900',
        className,
      )}
    >
      <span
        className="flex h-7 w-7 items-center justify-center rounded-md bg-brand-700 font-display text-sm font-bold text-white"
        aria-hidden
      >
        R
      </span>
      Rev<span className="text-brand-700">Base</span>
    </span>
  );
}

/** A consistent section heading block: brand eyebrow, DM Sans display headline,
 * and an optional lead paragraph. `align="center"` centers and width-limits the
 * block; the default left alignment is used in two-column section layouts. */
export function SectionHeading({
  eyebrow,
  title,
  lead,
  align = 'left',
  className,
}: {
  eyebrow: string;
  title: ReactNode;
  lead?: ReactNode;
  align?: 'left' | 'center';
  className?: string;
}) {
  return (
    <div className={clsx(align === 'center' && 'mx-auto max-w-2xl text-center', className)}>
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-slate-900 sm:text-4xl">
        {title}
      </h2>
      {lead && (
        <p
          className={clsx(
            'mt-4 text-lg leading-relaxed text-slate-600',
            align === 'center' && 'mx-auto max-w-xl',
          )}
        >
          {lead}
        </p>
      )}
    </div>
  );
}

/** The primary "Start free" CTA, matching the shared Button primitive exactly
 * (brand-700, px-5 py-2.5, text-sm font-semibold). Routes to the single auth
 * entry. `ringOffsetClass` lets a tinted band set the focus ring offset color. */
export function StartFreeButton({
  className,
  ringOffsetClass,
}: {
  className?: string;
  ringOffsetClass?: string;
}) {
  return (
    <Link
      to="/login"
      className={clsx(
        'inline-flex items-center justify-center gap-2 rounded-lg bg-brand-700 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
        ringOffsetClass,
        className,
      )}
    >
      Start free
    </Link>
  );
}

/** A secondary ghost anchor button, matching the shared Button ghost variant
 * (white, border-slate-300, slate-700). */
export function AnchorButton({
  href,
  children,
  className,
  ringOffsetClass,
}: {
  href: string;
  children: string;
  className?: string;
  ringOffsetClass?: string;
}) {
  return (
    <a
      href={href}
      className={clsx(
        'inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
        ringOffsetClass,
        className,
      )}
    >
      {children}
    </a>
  );
}
